import type { ResidentItem } from '@lmc/ts-common'
import { Avatar, Heading, Tag, Text } from '@lmc/ui-kit'
import { isDnacprScheduled } from '@lmc/validations'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import type { ResidentItemExtended } from 'hooks/useResident'
import type React from 'react'
import styled from 'styled-components'
import { shouldDisplayDnacpr } from '../../ResidentProfileScreen/utils/dnacpr'
import { shouldShowDolsBadge } from '../../ResidentProfileScreen/utils/dols'
import { getResidentStatus } from '../utils'

type ResidentListHeaderProps = {
  resident: ResidentItem
  onClick: React.MouseEventHandler<HTMLDivElement>
  firstItem?: boolean
  testId?: string
  index: number
}

const ResidentListItem = ({
  resident,
  onClick,
  firstItem,
  testId,
  index
}: ResidentListHeaderProps) => {
  const { isDoLSOverride } = useHomeType()
  const dolsModsAlignment = useFeatureFlags({
    flagNames: ['dols_mods_alignment']
  }).data?.dols_mods_alignment.enabled

  const picture = resident?.picture as unknown as string
  const name = `${resident?.name?.first} ${resident?.name?.last}`
  const hasAllergies = resident.residentAllergies?.length > 0
  const hasDnacpr = shouldDisplayDnacpr(
    resident.dnacpr,
    resident.dnacprStartDate
  )
  const hasUpcomingDnacpr =
    !hasDnacpr &&
    ((resident as ResidentItemExtended).dnacprScheduled ??
      isDnacprScheduled(
        resident?.dnacpr,
        resident?.dnacprStartDate ?? undefined
      ))

  const hasDols = shouldShowDolsBadge(resident, dolsModsAlignment)
  const hasBadges = hasDols || hasAllergies || hasDnacpr || hasUpcomingDnacpr

  return (
    <StyledPaddingWrapper
      data-testid={
        testId
          ? `${testId}-item-wrapper-${index}`
          : `resident-item-wrapper-${index}`
      }
    >
      <StyledContainer
        onClick={onClick}
        $firstItem={firstItem}
        data-testid={
          testId ? `${testId}-item-${index}` : `resident-item-${index}`
        }
      >
        <Avatar
          url={picture}
          size={40}
          data-testid={
            testId ? `${testId}-avatar-${index}` : `resident-avatar-${index}`
          }
        />
        <StyledHeading
          level={5}
          data-testid={
            testId ? `${testId}-name-${index}` : `resident-name-${index}`
          }
        >
          {name}
        </StyledHeading>
        <StyledRowWrapper
          data-testid={
            testId
              ? `${testId}-status-row-${index}`
              : `resident-status-row-${index}`
          }
        >
          {hasBadges && (
            <StyledBadges
              data-testid={
                testId
                  ? `${testId}-badges-${index}`
                  : `resident-badges-${index}`
              }
            >
              {hasDnacpr && (
                <Tag
                  color="red"
                  data-testid={
                    testId
                      ? `${testId}-dnacpr-tag-${index}`
                      : `resident-dnacpr-tag-${index}`
                  }
                >
                  DNACPR
                </Tag>
              )}
              {hasUpcomingDnacpr && (
                <Tag
                  color="gold"
                  data-testid={
                    testId
                      ? `${testId}-upcoming-dnacpr-tag-${index}`
                      : `resident-upcoming-dnacpr-tag-${index}`
                  }
                >
                  Upcoming DNACPR
                </Tag>
              )}
              {hasDols && (
                <Tag
                  color="blue"
                  data-testid={
                    testId
                      ? `${testId}-dols-tag-${index}`
                      : `resident-dols-tag-${index}`
                  }
                >
                  {isDoLSOverride ? 'DoLS' : 'LPS'}
                </Tag>
              )}
              {hasAllergies && (
                <Tag
                  color="green"
                  data-testid={
                    testId
                      ? `${testId}-allergies-tag-${index}`
                      : `resident-allergies-tag-${index}`
                  }
                >
                  Allergies
                </Tag>
              )}
            </StyledBadges>
          )}
          <StyledWrapper
            data-testid={
              testId
                ? `${testId}-status-wrapper-${index}`
                : `resident-status-wrapper-${index}`
            }
          >
            <StyledText
              level={5}
              data-testid={
                testId
                  ? `${testId}-status-${index}`
                  : `resident-status-${index}`
              }
            >
              {getResidentStatus(resident)}
            </StyledText>
          </StyledWrapper>
        </StyledRowWrapper>
      </StyledContainer>
    </StyledPaddingWrapper>
  )
}

const StyledContainer = styled.div<{ $firstItem?: boolean }>`
  background-color: ${(props) => props.theme.palette.white} !important;
  border-radius: 8px;
  margin-bottom: 10px;
  margin-top: ${(props) => (props?.$firstItem ? '24px' : '0px')};
  height: 64px;
  display: flex;
  align-items: center;
  padding: 16px 12px;
  max-width: 1080px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  cursor: pointer;
  &:hover {
    background-color: ${(props) =>
      props.theme.color.neutral.color10} !important;
  }
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-top: 0px;
  }
`

const StyledWrapper = styled.div`
  display: flex;
  margin-right: 0px;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: none;
  }
`

const StyledBadges = styled.div`
  display: flex;
  margin-left: auto;
  padding-right: 10px;
  border-right: 1px solid #ded5e6;
  height: fit-content;
  min-width: 100px;
  justify-content: flex-end;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    display: none;
  }
`

const StyledHeading = styled(Heading)`
  margin-left: 10px;
`

const StyledRowWrapper = styled.div`
  display: flex;
  margin-left: auto;
  align-items: center;
`

const StyledText = styled(Text)`
  font-size: 12px !important;
  min-width: 100px;
  text-align: center;
`

const StyledPaddingWrapper = styled.div`
  padding-left: 24px;
  padding-right: 24px;
`

export default ResidentListItem
